import { Pipe, PipeTransform } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Duration } from 'luxon';

export enum StopDurationFormat {
  HoursMinutes = 'hh:mm',
  Hours = 'hh.hh',
  Minutes = 'mm',
}

/**
 * Gets the StopDurationFormat enum value for the string passed in.
 * Returns null if the string doesn't match any of the stop duration formats (hh:mm, h.hh, or mm)
 *
 * @param duration string
 * @returns
 */
export function getStopDurationFormat(
  duration: string
): StopDurationFormat | null {
  if (!duration) {
    return null;
  }

  //hh:mm
  if (/^[0-9]{1,}:[0-9]{2}$/.exec(duration) !== null) {
    return StopDurationFormat.HoursMinutes;
  }
  //hh.hh
  if (/^[0-9]{0,}\.[0-9]+$/.exec(duration) !== null) {
    return StopDurationFormat.Hours;
  }
  //mm
  if (/^[0-9]+$/.exec(duration) !== null) {
    return StopDurationFormat.Minutes;
  }

  return null;
}

/**
 * Converts an accepted format string (hh:mm, h.hh, or mm) into a decimal value
 * representing the hours
 *
 * @param timeString String matching one of the stop duration formats (hh:mm, h.hh, or mm)
 * @returns Decimal value representing the hours of the stop duration
 */
export function getHoursFromString(timeString: string): number {
  const format = getStopDurationFormat(timeString);

  if (format === StopDurationFormat.HoursMinutes) {
    const [hh, mm] = timeString.split(':');
    return Number(hh) + Number(mm) / 60;
  } else if (format === StopDurationFormat.Hours) {
    return Number(timeString);
  } else if (format === StopDurationFormat.Minutes) {
    return Number(timeString) / 60;
  }

  return 0;
}

/**
 * Creates a new Duration object with any seconds rounded to the nearest minute
 * This is needed because formatting with floor set to false will return decimal minutes
 *
 * @param duration Duration object to be rounded
 * @returns New Duration object with any seconds rounded to the nearest minute
 */
export function roundMinutes(duration: Duration): Duration {
  const shifted = duration.shiftTo('hours', 'minutes', 'seconds');
  const seconds = shifted.seconds;
  const minutes = seconds > 30 ? 1 : 0;

  return shifted
    .plus({ minutes })
    .minus({ seconds })
    .shiftTo('hours', 'minutes');
}

/**
 * Converts a StopState.stopTime string into a string for
 * diplay in the stop list time column
 *
 * hh:mm '1:45' => '1:45'
 * hh.hh '1.5'  => '1:30'
 * mm    '15'   => '0:15'
 */
@Pipe({ name: 'stopDuration' })
export class StopDurationPipe implements PipeTransform {
  transform(value: string) {
    const hours = roundMinutes(
      Duration.fromObject({
        hours: getHoursFromString(value),
      })
    );

    return hours.toFormat('h:mm');
  }
}

/**
 * Angular validator for stop duration input. Will validate if the value
 * entered matches any of the accepted formats (hh:mm, h.hh, or mm)
 */
export function stopDurationValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = control.value;

    if (!val) {
      return null;
    }

    return getStopDurationFormat(val) === null
      ? {
          error: 'Invalid stop duration.',
        }
      : null;
  };
}
